import React from 'react';
import { FaCheckCircle, FaRegCircle } from 'react-icons/fa';
import FormSelect from '@/components/UI/FormSelect';
import { Card, OptionBuyingStrategySelect } from '@/components/UI';
import TradeLogicAttachmentCard from '@/features/tradeLogicAuthoring/components/TradeLogicAttachmentCard';
import type { TradeLogicAttachmentChange } from '@/features/tradeLogicAuthoring/components/attachmentEditorState';
import { listRegimePinDescriptions } from '../regimePinsPresentation';
import { parseTradingStrategyDurationInput, type TradingStrategyFormData } from '../tradingStrategyEditorSubmitPayloads';
import BasicInformationCard from './BasicInformationCard';
import StrategyLegsSection from './StrategyLegsSection';
import { durationOptions, entityTypeOptions, futuresDurationOptions } from './constants';

interface StrategyCreatePaneProps {
    formData: TradingStrategyFormData;
    nameInputRef: React.RefObject<HTMLInputElement | null>;
    setFormData: React.Dispatch<React.SetStateAction<TradingStrategyFormData>>;
    setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    strategyHasOptionLeg: boolean;
    validationErrors: Record<string, string>;
    onEntryAttachmentChange: (change: TradeLogicAttachmentChange) => void;
    onExitAttachmentChange: (change: TradeLogicAttachmentChange) => void;
    onInputChange: <Field extends keyof TradingStrategyFormData>(field: Field, value: TradingStrategyFormData[Field]) => void;
}

const ChecklistRow = ({ label, done }: { label: string; done: boolean }) => (
    <div className="flex items-center gap-2 text-xs">
        <span aria-hidden="true" className={done ? 'text-green-400' : 'text-zinc-600'}>
            {done ? <FaCheckCircle /> : <FaRegCircle />}
        </span>
        <span className={done ? 'text-zinc-200' : 'text-zinc-400'}>{label}</span>
    </div>
);

/**
 * Create mode is a short, single-pass form rather than the full section editor: screeners,
 * scorecards, exit candidates, session timing and backtest coverage all require a persisted
 * strategy, so asking for them before one exists only adds noise. Saving lands on the full editor.
 */
const StrategyCreatePane = ({
    formData,
    nameInputRef,
    setFormData,
    setValidationErrors,
    strategyHasOptionLeg,
    validationErrors,
    onEntryAttachmentChange,
    onExitAttachmentChange,
    onInputChange
}: StrategyCreatePaneProps) => {
    const visibleLegCount = formData.legs.filter((leg) => leg.isDeleted !== true).length;

    return (
        // The h5 override restores readable card titles; the global h5 rule paints them #7c8083.
        <div className="flex flex-col gap-5 [&_h5]:text-white">
            <Card subtitle="Everything else can be configured once the strategy exists." title="Before you can create this strategy">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <ChecklistRow done={formData.name.trim().length > 0} label="Name the strategy" />
                    <ChecklistRow done={formData.description.trim().length > 0} label="Describe the strategy" />
                    <ChecklistRow done={formData.tradeLogicV2EntryVersionId !== null} label="Select entry rules" />
                    <ChecklistRow done={formData.tradeLogicV2ExitVersionId !== null} label="Select exit rules" />
                    <ChecklistRow done={visibleLegCount > 0} label="Add at least one leg" />
                    <ChecklistRow done={formData.allowedUnderlyingEntityTypes.length > 0} label="Choose an entity type" />
                </div>
            </Card>

            {/* Only a copied strategy can carry pins here — fresh creates start with none and have no pin
                editor until the strategy exists. The note keeps a carried-over (possibly retired)
                classifier pin from riding invisibly into the new strategy. */}
            {formData.regimeModelVersionOverrides !== null && (
                <p className="text-xs text-zinc-500">
                    This copy carries the source strategy&rsquo;s regime model pins (
                    {listRegimePinDescriptions(formData.regimeModelVersionOverrides).join(', ')}). Review them under Setup &rarr; Regime
                    model pins after creating.
                </p>
            )}

            <BasicInformationCard
                formData={formData}
                nameInputRef={nameInputRef}
                validationErrors={validationErrors}
                onInputChange={onInputChange}
            />

            <Card subtitle="Which entities this strategy evaluates, and on what bar." title="Universe and timing">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormSelect
                        label="Primary duration"
                        options={
                            formData.allowedUnderlyingEntityTypes.includes('FuturesProduct') ? futuresDurationOptions : durationOptions
                        }
                        value={formData.duration}
                        onChange={(e) => onInputChange('duration', parseTradingStrategyDurationInput(e.target.value, formData.duration))}
                    />
                    <div>
                        <div className="mb-2 block text-sm font-medium text-zinc-300">Allowed entity types</div>
                        <div className="flex gap-4">
                            {entityTypeOptions.map((option) => (
                                <label className="flex items-center text-white" key={option.value}>
                                    <input
                                        checked={formData.allowedUnderlyingEntityTypes.includes(option.value)}
                                        className="mr-2"
                                        type="checkbox"
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                onInputChange(
                                                    'allowedUnderlyingEntityTypes',
                                                    option.value === 'AssetIndex' || option.value === 'FuturesProduct'
                                                        ? [option.value]
                                                        : [
                                                              ...formData.allowedUnderlyingEntityTypes.filter(
                                                                  (type) => type !== 'AssetIndex' && type !== 'FuturesProduct'
                                                              ),
                                                              option.value
                                                          ]
                                                );
                                            } else {
                                                onInputChange(
                                                    'allowedUnderlyingEntityTypes',
                                                    formData.allowedUnderlyingEntityTypes.filter((t) => t !== option.value)
                                                );
                                            }
                                        }}
                                    />
                                    {option.label}
                                </label>
                            ))}
                        </div>
                        {validationErrors.allowedUnderlyingEntityTypes && (
                            <div className="mt-1 text-sm text-red-400">{validationErrors.allowedUnderlyingEntityTypes}</div>
                        )}
                        {formData.allowedUnderlyingEntityTypes.includes('FuturesProduct') && (
                            <p className="mt-1 text-xs text-zinc-400">
                                Futures research uses one outright Future leg, 1m or 5m bars, exchange sessions and an inactive strategy.
                                Only 5m strategies can size by risk to a captured stop. Live trading and scheduled strategy backtests remain
                                disabled.
                            </p>
                        )}
                        {formData.allowedUnderlyingEntityTypes.includes('AssetIndex') && (
                            <p className="mt-1 text-xs text-zinc-400">
                                Index-option strategies remain inactive and use manual backtests only.
                            </p>
                        )}
                    </div>
                </div>
            </Card>

            <TradeLogicAttachmentCard
                canEdit
                definitionId={formData.entryLogicDefinitionId}
                kind="entry"
                version={formData.entryLogicVersion}
                versionId={formData.tradeLogicV2EntryVersionId}
                onChange={onEntryAttachmentChange}
            />

            <TradeLogicAttachmentCard
                canEdit
                definitionId={formData.exitLogicDefinitionId}
                kind="exit"
                version={formData.exitLogicVersion}
                versionId={formData.tradeLogicV2ExitVersionId}
                onChange={onExitAttachmentChange}
            />

            <StrategyLegsSection
                legs={formData.legs}
                setFormData={setFormData}
                setValidationErrors={setValidationErrors}
                sizingScope={formData}
                validationErrors={validationErrors}
            />

            {strategyHasOptionLeg && (
                <Card subtitle="Required because this strategy has an option leg." title="Backtest option buying strategies">
                    <OptionBuyingStrategySelect
                        error={validationErrors.autoBacktestOptionBuyingStrategiesOverride}
                        helpText="Leave empty only if every option leg sets its own override."
                        label="Option buying strategies (strategy default)"
                        mode="multi"
                        placeholder="Select option buying strategies..."
                        value={formData.autoBacktestOptionBuyingStrategiesOverride ?? []}
                        onChange={(selectedIds) => {
                            onInputChange('autoBacktestOptionBuyingStrategiesOverride', selectedIds.length > 0 ? selectedIds : undefined);
                        }}
                    />
                </Card>
            )}
        </div>
    );
};

export default StrategyCreatePane;
